import { analyticsManager, useErrorHelper, useMessageDisplayer } from '@inpart-io/devtools-vue';
import { DataType, type ILead } from '@inpart-io/padme-iap';
import { privateDocumentProperties } from '@inpart-io/padme-iap';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import EmailImport from '@/components/email-import-dialog/email-import-dialog.vue';
import { usePaginationStoredLocator } from '@/components/pagination/composables/usePaginationStoreLocator';
import { ANALYTICS_EVENTS, LAYOUT_TYPE } from '@/constants/analytics.constants';
import { MAX_FILE_SIZE, MIN_FILE_SIZE, VALID_FILE_NAME } from '@/constants/attachments.constants';
import { errorTypeCodes } from '@/constants/error-type.constants';
import { STRING_REGEX } from '@/constants/validation-regex.constants';
import { UPLOAD_FILE_TO_OBJECT } from '@/store/base/binary-uploads/actions';
import { IMPORT_STATUS_MODULE } from '@/store/import-status-module';
import { PARSE_EMAIL } from '@/store/import-status-module/actions';
import { PRIVATE_DOCUMENT_MODULE } from '@/store/private-document-module';

interface IEmailImport {
    importedEmails: number[];
    importedAttachments: number[];
}

export function useLeadUploadHelper(): {
    displayUploadError: (file: File, error: any) => void;
    processUploadQueue: (
        files: File[],
        containerType: string,
        containerId: number,
        folderId?: number,
        lead?: ILead,
        trackEvent?: boolean
    ) => Promise<unknown[]>;
    uploadFileToLead: (files: File, containerType: string, containerId: number) => Promise<number>;
} {
    const store = useStore();
    const q = useQuasar();
    const { t } = useI18n();
    const messageDisplayer = useMessageDisplayer();
    const errorHelper = useErrorHelper();
    const route = useRoute();
    const { delayedRefreshPage } = usePaginationStoredLocator(DataType.GroupedDataObjectIdentity, { getActions: true });

    function displayUploadError(file: File, error: any): void {
        if (file.size < MIN_FILE_SIZE) {
            // Error: size of file is too small
            messageDisplayer.error(t('FILE.ERROR.MIN_SIZE_ERROR', { fileName: file.name.length > 50 ? file.name.substring(0, 50) : file.name }).toString());
        } else if (file.size > MAX_FILE_SIZE) {
            // Error: size of file is too small
            messageDisplayer.error(t('FILE.ERROR.MAX_SIZE_ERROR', { fileName: file.name.length > 50 ? file.name.substring(0, 50) : file.name }).toString());
        } else if (file.name.split('.').length === 0) {
            // Error: the extension is missing
            messageDisplayer.error(t('FILE.ERROR.MISSING_TYPE_ERROR', { fileName: file.name.length > 50 ? file.name.substring(0, 50) : file.name }).toString());
        } else if (file.name.split('.').length > 2) {
            // Error: can't upload multiple extensions file
            messageDisplayer.error(
                t('FILE.ERROR.MULTIPLE_EXTENSIONS_ERROR', { fileName: file.name.length > 50 ? file.name.substring(0, 50) : file.name }).toString()
            );
        } else if (
            // Error: some special characters in the file name are not supported
            !file.name.match(VALID_FILE_NAME)
        ) {
            messageDisplayer.error(t('FILE.ERROR.UNSUPPORTED_CHARACTERS_ERROR').toString());
        } else if (error.status === errorTypeCodes.VIRUS) {
            messageDisplayer.error(t('FILE.ERROR.VIRUS', { fileName: file.name.length > 50 ? file.name.substring(0, 50) : file.name }).toString());
        } else {
            errorHelper.handleError(error, t('ATTACHMENT.LOW.SING'));
        }
    }

    async function processUploadQueue(
        files: File[],
        containerType: string,
        containerId: number,
        folderId?: number,
        lead?: ILead,
        trackEvent = true
    ): Promise<any[]> {
        const emails: File[] = [];
        const docs = [];
        const uploadedFilesIds: number[] = [];
        for (const f of files) {
            if (f.name.match(STRING_REGEX.EMAIL_FILE)) {
                emails.push(f);
            } else {
                docs.push(f);
            }
        }
        const uploadPromises = [] as Array<Promise<any>>;
        if (emails.length > 0) {
            const emailImportPromise = uploadEmailsToLead(emails, containerType, containerId, folderId, lead).catch((error) => {
                if (error.status === errorTypeCodes.VIRUS) {
                    messageDisplayer.error(
                        t('FILE.ERROR.VIRUS', {
                            fileName: emails
                                .map((email) => {
                                    return email.name;
                                })
                                .join(', ')
                        }).toString()
                    );
                } else {
                    errorHelper.handleError(error, t('EMAIL.LOW.SING'));
                }
            });
            uploadPromises.push(emailImportPromise);
        }

        if (docs.length > 0) {
            for (const d of docs) {
                const docPromise = uploadFileToLead(d, containerType, containerId, folderId)
                    .then((fileId: number) => {
                        uploadedFilesIds.push(fileId);
                        messageDisplayer.success(t('FILE.UPLOAD_SUCCESS').toString());
                        return fileId;
                    })
                    .catch((error) => {
                        displayUploadError(d, error);
                    });
                uploadPromises.push(docPromise);
            }
            if (trackEvent) {
                trackLeadAttachmentsEvent({
                    lead,
                    eventName: ANALYTICS_EVENTS.LEAD_ATTACHMENTS.UPLOAD,
                    numberOfFiles: docs.length
                });
            }
        }
        return Promise.all(uploadPromises).then((importResult: any[]) => {
            delayedRefreshPage();

            if (emails.length > 0) {
                const finalUploadList = [] as any[];
                const emailUploadResult = importResult.shift();
                finalUploadList.push(emailUploadResult.importedEmails);
                finalUploadList.push(...uploadedFilesIds);
                return finalUploadList;
            } else {
                return [[], ...uploadedFilesIds];
            }
        });
    }

    async function uploadFileToLead(file: File, containerType: string, containerId: number, folderId?: number): Promise<number> {
        return store.dispatch(`${PRIVATE_DOCUMENT_MODULE}/${UPLOAD_FILE_TO_OBJECT}`, {
            objectId: containerId,
            objectType: containerType,
            file,
            folderId,
            newFileName: null,
            properties: privateDocumentProperties.DEFAULT
        });
    }

    async function uploadEmailsToLead(
        emails: File[],
        containerType: string,
        containerId: number,
        folderId?: number,
        lead?: ILead,
        skipDialog?: boolean
    ): Promise<IEmailImport> {
        const currentAsyncTaskId = await store.dispatch(`${IMPORT_STATUS_MODULE}/${PARSE_EMAIL}`, {
            containerId,
            containerType,
            files: emails,
            folderId
        });

        trackLeadAttachmentsEvent({
            lead,
            eventName: ANALYTICS_EVENTS.EMAIL_IMPORT.UPLOAD,
            numberOfFiles: emails.length
        });

        return new Promise((resolve) => {
            q.dialog({
                component: EmailImport,
                persistent: true,
                componentProps: { properties: { executionId: currentAsyncTaskId }, folderId }
            })
                .onOk((imported: { importedEmails: number[]; importedAttachments: number[] }) => {
                    resolve(imported);
                })
                .onCancel(() => {
                    resolve({ importedEmails: [], importedAttachments: [] });
                });
        });
    }

    function trackLeadAttachmentsEvent({
        lead,
        eventName,
        pageCount,
        currentPage,
        numberOfFiles
    }: {
        lead: ILead;
        eventName: string;
        pageCount?: number;
        currentPage?: number;
        numberOfFiles?: number;
    }): void {
        analyticsManager.trackAction(eventName, {
            toPage: currentPage,
            totalPages: pageCount,
            numberOfFiles,
            leadSource: lead?.source,
            leadStatus: lead?.status,
            leadType: lead?.assetProfileEntity?.type,
            layoutType: route.query?.statusTab ? LAYOUT_TYPE.LEAD_DRAWER : LAYOUT_TYPE.LEAD_PAGE
        });
    }

    return { displayUploadError, processUploadQueue, uploadFileToLead };
}
